import { useFormik } from 'formik'
import React, { useState } from 'react'

import { AddStakeholderToFulfilmentCentreSchema } from '../../validation/validate'

import Dropdown from '../inputs/dropdown'
import { EditInput } from '../inputs/editInput'
import { TextInput } from '../inputs/textInput'
import Icon from '../icon'
import { NumericFormat } from 'react-number-format'
import {
  useAssignFulfillmentCentreMutation,
  useUpdateAssignFulfillmentCentreMutation,
} from '../../redux-toolkits/investor/investor.slice'
import { TableComponent } from '../table'
import { IFC } from '../../redux-toolkits/investor/investor.type'
import { Button } from '../button'

const settlementSchedule = [
  {
    name: 'INSTANT',
    value: 'INSTANT',
  },
  {
    name: 'TIME BASED',
    value: 'TIME_BASED',
  },
  {
    name: 'PERIOD BASED',
    value: 'PERIOD_BASED',
  },
]

const settlementPeriod = [
  {
    name: 'DAY',
    value: 'DAY',
  },
  {
    name: 'WEEK',
    value: 'WEEK',
  },
  {
    name: 'MONTH',
    value: 'MONTH',
  },
  {
    name: 'QUARTER',
    value: 'QUARTER',
  },
  {
    name: 'YEAR',
    value: 'YEAR',
  },
]

interface StakeholderFulfilmentCenterProps {
  stakeholderId: string
  centers: { name: string; value: string }[]
  fcs: IFC[]
  isFetching: boolean
  setSelectedFC: React.Dispatch<React.SetStateAction<IFC | null>>
  selectedFC: IFC | null
  onSuccess?: () => void
  setOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>
}

const StakeholderFulfilmentCenter: React.FC<
  StakeholderFulfilmentCenterProps
> = ({
  centers,
  stakeholderId,
  fcs,
  isFetching,
  selectedFC,
  setSelectedFC,
  onSuccess,
  setOpenDeleteModal,
}) => {
  const [editing, setEditing] = useState(false)

  const [assignFulfillmentCentre, { isLoading: assignLoading }] =
    useAssignFulfillmentCentreMutation()

  const [updateFC, { isLoading: isUpdatingFC }] =
    useUpdateAssignFulfillmentCentreMutation()

  const handleOnSuccess = () => {
    if (typeof onSuccess === 'function') {
      onSuccess()
    }
  }

  const {
    values,
    setFieldValue,
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
    resetForm,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      fulfilmentCentreId: selectedFC?.fulfilmentCentre.name ?? '',
      splitPercentage: String(selectedFC?.splitPercentage) ?? '',
      settlementSchedule: selectedFC?.settlementSchedule ?? '',
      settlementDelayDays: selectedFC?.settlementDelayDays ?? '',
      settlementPeriod: selectedFC?.settlementPeriod ?? '',
      fulfilmentCentreValue: '',
    },
    validationSchema: AddStakeholderToFulfilmentCentreSchema,
    onSubmit: async (vals) => {
      const payload = {
        settlementPeriod: vals.settlementPeriod,
        settlementSchedule: vals.settlementSchedule,
        fulfilmentCentreId: vals.fulfilmentCentreValue,
        settlementDelayDays: Number(vals.settlementDelayDays),
        splitPercentage: Number(vals.splitPercentage.split('%').join('')),
      }

      if (selectedFC?._id) {
        const { fulfilmentCentreId, ...newPayload } = payload
        const res = await updateFC({
          payload: newPayload,
          id: stakeholderId,
          fcId: selectedFC.fulfilmentCentre._id,
        })
        if (res.data?.success) {
          setEditing(!editing)
          handleOnSuccess()
          resetForm()
          setSelectedFC(null)
          return
        }
      } else {
        const res = await assignFulfillmentCentre({ payload, stakeholderId })
        if (res.data?.success) {
          setEditing(!editing)
          handleOnSuccess()
          resetForm()
          setSelectedFC(null)
          return
        }
      }
    },
  })

  return (
    <div>
      <div className="flex flex-row items-center justify-between my-4">
        <p>Stakeholder Fulfilment Centres</p>

        <button
          type="button"
          onClick={() => {
            setEditing(!editing)
            resetForm()
            setSelectedFC(null)
          }}
          className="flex items-center gap-2"
        >
          {editing ? (
            <span>Cancel</span>
          ) : (
            <>
              <span>Add</span> <Icon id="plus" width={20} height={20} />
            </>
          )}
        </button>
      </div>

      <div className="pb-4 border-b">
        <EditInput
          onEditSave={() => {
            handleSubmit()
          }}
          editing={editing}
          onEditClick={() => {
            setEditing(true)
          }}
          onCancel={() => {
            setEditing(false)
          }}
          title="Add New Fulfilment Centre"
          loading={assignLoading}
          mode="new"
        >
          {({ isEditing }) => {
            return isEditing ? (
              <>
                <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Dropdown
                    loading={false}
                    disabled={!!selectedFC?._id}
                    className=""
                    menuClassName="!max-h-[200px]"
                    menuButton={
                      <TextInput
                        type="text"
                        name="fulfilmentCentreId"
                        title="Fulfilment Centre"
                        placeholder="Choose Fulfilment Centre"
                        value={values.fulfilmentCentreId}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        errors={
                          errors.fulfilmentCentreId &&
                          touched.fulfilmentCentreId
                            ? errors.fulfilmentCentreId
                            : ''
                        }
                        hasIcon={false}
                        readOnly
                        extra={
                          <>
                            {!selectedFC?._id && (
                              <Icon
                                id={'left-caret'}
                                width={16}
                                height={16}
                                className="-rotate-90 text-sec-black"
                              />
                            )}
                          </>
                        }
                      />
                    }
                    menuItems={centers.map((opt) => ({
                      ...opt,
                      onClick: () => {
                        setFieldValue('fulfilmentCentreValue', opt.value)
                        setFieldValue('fulfilmentCentreId', opt.name)
                      },
                    }))}
                  />

                  <NumericFormat
                    suffix={'%'}
                    customInput={TextInput}
                    type="text"
                    name="splitPercentage"
                    title="Split Percentage"
                    placeholder="Enter Split Percentage"
                    value={values.splitPercentage}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    errors={
                      errors.splitPercentage && touched.splitPercentage
                        ? errors.splitPercentage
                        : ''
                    }
                    hasIcon={false}
                    decimalScale={2}
                    thousandSeparator
                  />

                  <Dropdown
                    loading={false}
                    className=""
                    menuClassName="!max-h-[200px]"
                    menuButton={
                      <TextInput
                        type="text"
                        name="settlementSchedule"
                        title="Settlement Schedule"
                        placeholder="Choose Settlement Schedule"
                        value={values.settlementSchedule}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        errors={
                          errors.settlementSchedule &&
                          touched.settlementSchedule
                            ? errors.settlementSchedule
                            : ''
                        }
                        hasIcon={false}
                        readOnly
                        extra={
                          <Icon
                            id={'left-caret'}
                            width={16}
                            height={16}
                            className="-rotate-90 text-sec-black"
                          />
                        }
                      />
                    }
                    menuItems={settlementSchedule.map((opt) => ({
                      ...opt,
                      onClick: () => {
                        // setFieldValue('type', opt.value)
                        setFieldValue('settlementSchedule', opt.value)
                      },
                    }))}
                  />

                  <NumericFormat
                    suffix={undefined}
                    customInput={TextInput}
                    type="text"
                    name="settlementDelayDays"
                    title="Settlement Delay Days"
                    placeholder="Settlement Delay Days"
                    value={values.settlementDelayDays}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    errors={
                      errors.settlementDelayDays && touched.settlementDelayDays
                        ? errors.settlementDelayDays
                        : ''
                    }
                    hasIcon={false}
                    decimalScale={2}
                    thousandSeparator
                  />

                  <Dropdown
                    loading={false}
                    className=""
                    menuClassName="!max-h-[200px]"
                    menuButton={
                      <TextInput
                        type="text"
                        name="settlementPeriod"
                        title="Settlement Period"
                        placeholder="Choose Settlement Period"
                        value={values.settlementPeriod}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        errors={
                          errors.settlementPeriod && touched.settlementPeriod
                            ? errors.settlementPeriod
                            : ''
                        }
                        hasIcon={false}
                        readOnly
                        extra={
                          <Icon
                            id={'left-caret'}
                            width={16}
                            height={16}
                            className="-rotate-90 text-sec-black"
                          />
                        }
                      />
                    }
                    menuItems={settlementPeriod.map((opt) => ({
                      ...opt,
                      onClick: () => {
                        // setFieldValue('type', opt.value)
                        setFieldValue('settlementPeriod', opt.name)
                      },
                    }))}
                  />
                </div>

                <Button
                  type="button"
                  primary
                  loading={assignLoading || isUpdatingFC}
                  disabled={assignLoading || isUpdatingFC}
                  label="Save"
                  className="!max-w-[160px]"
                  onClick={() => {
                    handleSubmit()
                  }}
                />
              </>
            ) : (
              <div>
                {/* {values.value
                  ? `${values.value} • ${values?.type}`
                  : 'Not Configured'} */}
              </div>
            )
          }}
        </EditInput>
      </div>

      <div className="w-full overflow-x-scroll pt-4">
        <TableComponent
          loading={isFetching}
          headers={[
            'Fulfilment Centre Name',
            'Settlement Delay Days',
            'Settlement Period',
            'Settlement Schedule',
            'Split Percentage',
            'Actions',
          ]}
          rows={
            fcs?.map((fc) => {
              return {
                id: fc._id,
                content: [
                  fc.fulfilmentCentre.name,
                  fc.settlementDelayDays,
                  fc.settlementPeriod,
                  fc.settlementSchedule,
                  `${fc.splitPercentage}%`,
                  <Dropdown
                    className="text-left"
                    key={`${fc._id}-controls`}
                    menuButton={
                      <Icon id="ellipses" height={18} width={18} className="" />
                    }
                    menuItems={[
                      {
                        name: 'Edit',
                        value: '',
                        onClick: () => {
                          setSelectedFC(fc)
                          setEditing(true)
                        },
                      },
                      {
                        name: 'Delete',
                        value: '',
                        onClick: () => {
                          setSelectedFC(fc)
                          setOpenDeleteModal(true)
                        },
                      },
                    ]}
                  />,
                ],
              }
            }) ?? []
          }
          isEmpty={[0, undefined].includes(fcs?.length)}
        />
      </div>
    </div>
  )
}

export default StakeholderFulfilmentCenter
